const PGAdapter = require('./../../common/pg-adapter')
const DATA_ORDER_TABLE_NAME = 'avocado.data_order'
const DATA_ORDER_COLUMNS = [
  'id',
  'order_id',
  'sub_order_id',
  'group_id',
  'funder_id',
  'order_type',
  'status',
  'destination_id',
  'created_at',
  'created_by',
  'updated_at',
  'updated_by',
  'closed_at',
  'closed_by',
  'closed_status',
  'delivery_date',
  'is_complete',
  'rds_created_at',
  'adjusted_at',
  'approved_at',
  'received_at',
  'program_id',
  'supplier_id',
  'delivery_fee',
  'payment_choice',
  'route_id'
]
// in weeks
const ORDER_CONFIRMATION_WINDOW = 2

class DataOrderAdapter extends PGAdapter {
  constructor (pgConnection, username, logger) {
    super(
      pgConnection,
      DATA_ORDER_TABLE_NAME,
      username,
      DATA_ORDER_COLUMNS,
      'order_id',
      null,
      logger
    )
  }

  async getOrder ({
    orderId
  }) {
    if (!orderId) {
      throw new Error(`orderId is required`)
    }

    let query = `
      SELECT *
      FROM ${DATA_ORDER_TABLE_NAME}
      WHERE order_id = $1
    `
    const { rows } = await this.pgConnection.query(query, [orderId])
    return rows
  }

  // Returns order snapshots in the same camelCase shape as the PouchDB adapter,
  // so docsToOrderEntities can process them transparently.
  // We also include shipments from data_shipment and data_shipmentline tables for accepted orders

  async listFacilityOrdersByStatus ({ programId, locationId, statuses, isComplete, limit, shipmentStatus, excludeShipmentStatus }) {
    const queryParams = [programId, locationId, statuses]

    let isCompleteClause = ''
    if (typeof isComplete === 'boolean') {
      queryParams.push(isComplete)
      isCompleteClause = `AND o.is_complete = $${queryParams.length}`
    }

    let shipmentStatusClause = ''
    if (shipmentStatus) {
      queryParams.push(shipmentStatus)
      shipmentStatusClause = `AND s.status = $${queryParams.length}`
    } else if (excludeShipmentStatus) {
      queryParams.push(excludeShipmentStatus)
      shipmentStatusClause = `AND (s.status IS NULL OR s.status != $${queryParams.length})`
    }

    let limitClause = ''
    if (limit) {
      queryParams.push(limit)
      limitClause = `LIMIT $${queryParams.length}`
    }

    const query = `
      WITH filtered_orders AS (
        SELECT
          o.id,
          o.order_id,
          o.sub_order_id,
          o.group_id,
          o.program_id,
          o.destination_id,
          o.supplier_id,
          o.funder_id,
          o.order_type,
          o.status,
          o.is_complete,
          o.created_at,
          o.created_by,
          o.updated_at,
          o.updated_by,
          o.closed_at,
          o.closed_by,
          o.closed_status,
          o.delivery_date,
          o.route_id,
          o.payment_choice,
          o.delivery_fee,
          s.id AS shipment_id,
          s.shipment_id AS shipment_text_id,
          s.status AS shipment_status
        FROM ${DATA_ORDER_TABLE_NAME} o
        LEFT JOIN avocado.data_shipment s ON s.suborder_id = o.sub_order_id
        WHERE o.program_id = $1
          AND o.destination_id = $2
          AND o.status = ANY($3::text[])
          ${isCompleteClause}
          ${shipmentStatusClause}
        ORDER BY o.created_at DESC
        ${limitClause}
      ),
      latest_prices AS (
        SELECT DISTINCT ON (ph.location_id, ph.sku)
          ph.location_id,
          ph.sku,
          ph.sellprice
        FROM real.price_history ph
        WHERE ph.location_id IN (
          SELECT loc.uuid
          FROM filtered_orders fo
          JOIN avocado.data_location loc ON loc.fsid = fo.supplier_id
        )
        AND ph.sku IN (
          SELECT ol.sku FROM avocado.data_orderline ol
          WHERE ol.order_id IN (SELECT id FROM filtered_orders)
          UNION
          SELECT sl.sku FROM avocado.data_shipmentline sl
          WHERE sl.shipment_id IN (SELECT shipment_id FROM filtered_orders WHERE shipment_id IS NOT NULL)
        )
        ORDER BY ph.location_id, ph.sku, ph.date DESC
      )
      SELECT
        fo.order_id        AS "orderId",
        fo.sub_order_id    AS "suborderId",
        fo.group_id        AS "groupId",
        fo.program_id      AS "programId",
        fo.destination_id  AS "destinationId",
        fo.supplier_id     AS "supplierId",
        fo.funder_id       AS "funderId",
        fo.order_type      AS "orderType",
        fo.status,
        fo.is_complete     AS "isComplete",
        fo.created_at      AS "createdAt",
        fo.created_by      AS "createdBy",
        fo.updated_at      AS "updatedAt",
        fo.updated_by      AS "updatedBy",
        fo.closed_at       AS "closedAt",
        fo.closed_by       AS "closedBy",
        fo.closed_status   AS "closedStatus",
        fo.delivery_date   AS "deliveryDate",
        fo.route_id        AS "routeId",
        fo.payment_choice  AS "paymentChoice",
        fo.delivery_fee    AS "deliveryFee",
        fo.shipment_status AS "shipmentStatus",
        COALESCE(sl_agg.products, ol_agg.products, '{}'::json) AS products,
        COALESCE(sl_agg."totalAmount", ol_agg."totalAmount", 0) AS "totalAmount",
        COALESCE(ss_agg."shipmentSnapshotsByStatus", '{}'::json) AS "shipmentSnapshotsByStatus",
        ss_agg."shipmentSnapshotId"
      FROM filtered_orders fo
      LEFT JOIN avocado.data_location loc ON loc.fsid = fo.supplier_id
      LEFT JOIN LATERAL (
        SELECT
          json_object_agg(
            'product:' || ol.sku,
            json_build_object('original', ol.original, 'adjusted', COALESCE(ol.approved, ol.adjusted))
          ) AS products,
          SUM(COALESCE(ol.approved, ol.adjusted, 0) * COALESCE(lp.sellprice, 0)) AS "totalAmount"
        FROM avocado.data_orderline ol
        LEFT JOIN latest_prices lp ON lp.sku = ol.sku AND lp.location_id = loc.uuid
        WHERE ol.order_id = fo.id
      ) ol_agg ON true
      LEFT JOIN LATERAL (
        SELECT
          json_object_agg(
            'product:' || sl.sku,
            json_build_object('original', 0, 'adjusted', sl.amount)
          ) AS products,
          SUM(sl.amount * COALESCE(lp.sellprice, 0)) AS "totalAmount"
        FROM avocado.data_shipmentline sl
        LEFT JOIN latest_prices lp ON lp.sku = sl.sku AND lp.location_id = loc.uuid
        WHERE sl.shipment_id = fo.shipment_id
      ) sl_agg ON true
      LEFT JOIN LATERAL (
        SELECT
          json_object_agg(ss.status, ss.updated_at) AS "shipmentSnapshotsByStatus",
          MAX(ss.couchdb_id) FILTER (WHERE ss.status = fo.shipment_status) AS "shipmentSnapshotId"
        FROM avocado.data_shipmentsnapshot ss
        WHERE ss.shipment_id = fo.shipment_text_id
      ) ss_agg ON true
    `

    const { rows } = await this.pgConnection.query(query, queryParams)
    return rows
  }

  async getFacilityOrder ({ groupId }) {
    if (!groupId) {
      throw new Error('groupId is required')
    }

    const queryParams = [groupId]

    const query = `
      WITH filtered_orders AS (
        SELECT
          o.id,
          o.order_id,
          o.sub_order_id,
          o.group_id,
          o.program_id,
          o.destination_id,
          o.supplier_id,
          o.funder_id,
          o.order_type,
          o.status,
          o.is_complete,
          o.created_at,
          o.created_by,
          o.updated_at,
          o.updated_by,
          o.closed_at,
          o.closed_by,
          o.closed_status,
          o.delivery_date,
          o.route_id,
          o.payment_choice,
          o.delivery_fee,
          s.id AS shipment_id,
          s.shipment_id AS shipment_text_id,
          s.status AS shipment_status
        FROM ${DATA_ORDER_TABLE_NAME} o
        LEFT JOIN avocado.data_shipment s ON s.suborder_id = o.sub_order_id
        WHERE o.group_id = $1
        ORDER BY o.created_at DESC
      ),
      latest_prices AS (
        SELECT DISTINCT ON (ph.location_id, ph.sku)
          ph.location_id,
          ph.sku,
          ph.sellprice
        FROM real.price_history ph
        WHERE ph.location_id IN (
          SELECT loc.uuid
          FROM filtered_orders fo
          JOIN avocado.data_location loc ON loc.fsid = fo.supplier_id
        )
        AND ph.sku IN (
          SELECT ol.sku FROM avocado.data_orderline ol
          WHERE ol.order_id IN (SELECT id FROM filtered_orders)
          UNION
          SELECT sl.sku FROM avocado.data_shipmentline sl
          WHERE sl.shipment_id IN (SELECT shipment_id FROM filtered_orders WHERE shipment_id IS NOT NULL)
        )
        ORDER BY ph.location_id, ph.sku, ph.date DESC
      )
      SELECT
        fo.order_id        AS "orderId",
        fo.sub_order_id    AS "suborderId",
        fo.group_id        AS "groupId",
        fo.program_id      AS "programId",
        fo.destination_id  AS "destinationId",
        fo.supplier_id     AS "supplierId",
        fo.funder_id       AS "funderId",
        fo.order_type      AS "orderType",
        fo.status,
        fo.is_complete     AS "isComplete",
        fo.created_at      AS "createdAt",
        fo.created_by      AS "createdBy",
        fo.updated_at      AS "updatedAt",
        fo.updated_by      AS "updatedBy",
        fo.closed_at       AS "closedAt",
        fo.closed_by       AS "closedBy",
        fo.closed_status   AS "closedStatus",
        fo.delivery_date   AS "deliveryDate",
        fo.route_id        AS "routeId",
        fo.payment_choice  AS "paymentChoice",
        fo.delivery_fee    AS "deliveryFee",
        fo.shipment_status AS "shipmentStatus",
        COALESCE(sl_agg.products, ol_agg.products, '{}'::json) AS products,
        COALESCE(sl_agg."totalAmount", ol_agg."totalAmount", 0) AS "totalAmount",
        COALESCE(ss_agg."shipmentSnapshotsByStatus", '{}'::json) AS "shipmentSnapshotsByStatus",
        ss_agg."shipmentSnapshotId"
      FROM filtered_orders fo
      LEFT JOIN avocado.data_location loc ON loc.fsid = fo.supplier_id
      LEFT JOIN LATERAL (
        SELECT
          json_object_agg(
            'product:' || ol.sku,
            json_build_object('original', ol.original, 'adjusted', COALESCE(ol.approved, ol.adjusted))
          ) AS products,
          SUM(COALESCE(ol.approved, ol.adjusted, 0) * COALESCE(lp.sellprice, 0)) AS "totalAmount"
        FROM avocado.data_orderline ol
        LEFT JOIN latest_prices lp ON lp.sku = ol.sku AND lp.location_id = loc.uuid
        WHERE ol.order_id = fo.id
      ) ol_agg ON true
      LEFT JOIN LATERAL (
        SELECT
          json_object_agg(
            'product:' || sl.sku,
            json_build_object('original', 0, 'adjusted', sl.amount)
          ) AS products,
          SUM(sl.amount * COALESCE(lp.sellprice, 0)) AS "totalAmount"
        FROM avocado.data_shipmentline sl
        LEFT JOIN latest_prices lp ON lp.sku = sl.sku AND lp.location_id = loc.uuid
        WHERE sl.shipment_id = fo.shipment_id
      ) sl_agg ON true
      LEFT JOIN LATERAL (
        SELECT
          json_object_agg(ss.status, ss.updated_at) AS "shipmentSnapshotsByStatus",
          MAX(ss.couchdb_id) FILTER (WHERE ss.status = fo.shipment_status) AS "shipmentSnapshotId"
        FROM avocado.data_shipmentsnapshot ss
        WHERE ss.shipment_id = fo.shipment_text_id
      ) ss_agg ON true
    `

    const { rows } = await this.pgConnection.query(query, queryParams)
    return rows
  }

  // This is built specifically for the psm order export
  async listOrdersExport ({
    created_at__gte: greaterThan,
    created_at__lte: lessThan,
    search: destinationId,
    program_id__in: programs = [],
    funder_id__in: funders = [],
    ordering,
    limit,
    offset = 0
  }) {
    try {
      let query = `
        SELECT 
          o.*,
          json_agg(
            json_build_object(
              'sku', ol.sku,
              'original', ol.original,
              'adjusted', ol.adjusted,
              'approved', ol.approved,
              'received', ol.received,
              'allocation_type', ol.allocation_type,
              'comments_facility', ol.comments_facility,
              'comments_requester', ol.comments_requester,
              'comments_warehouse', ol.comments_warehouse
            )
          ) AS lines
        FROM avocado.data_order o
        INNER JOIN avocado.data_orderline ol -- We use inner join to exclude order rows without orderlines
          ON o.id = ol.order_id
        WHERE o.created_at >= $1
          AND o.created_at <= $2
      `

      const queryParams = [
        greaterThan,
        lessThan
      ]

      // Add destinationId (search) if provided
      if (destinationId) {
        query += ` AND destination_id ILIKE '%'||$${queryParams.length + 1}||'%'`
        queryParams.push(destinationId)
      }

      // Add programs filter if not empty
      if (programs && programs.length > 0) {
        query += ` AND program_id = ANY($${queryParams.length + 1}::text[])`
        queryParams.push(programs)
      }

      // Add funders filter if not empty
      if (funders && funders.length > 0) {
        query += ` AND funder_id = ANY($${queryParams.length + 1}::text[])`
        queryParams.push(funders)
      }

      // Add GROUP BY and ORDER BY clauses
      query += `
        GROUP BY 
          o.id, o.sub_order_id, o.group_id, o.funder_id, o.program_id, o.order_type, 
          o.status, o.destination_id, o.created_at, o.created_by, o.updated_at, 
          o.updated_by, o.closed_at, o.closed_by, o.closed_status, o.adjusted_at, 
          o.approved_at, o.received_at, o.delivery_date, o.is_complete
        ORDER BY o.destination_id
        LIMIT $${queryParams.length + 1} OFFSET $${queryParams.length + 2};
      `

      // Add limit and offset to the params array
      queryParams.push(limit, offset)

      // Execute the query
      const result = await this.pgConnection.query(query, queryParams)

      return {
        results: result.rows,
        next: result.rows.length === limit
          ? {
            created_at__gte: greaterThan,
            created_at__lte: lessThan,
            search: destinationId,
            program_id__in: programs,
            funder_id__in: funders,
            ordering,
            limit,
            offset: offset + limit
          }
          : null
      }
    } catch (err) {
      console.error('Error executing query:', err)
      throw err
    }
  }

  /**
   * @param {string} locationId
   * @returns {Promise<{locationId: string, orderId: string, deliveryAmount: number, deliveryInvoiceAmount: number}[]>}
  */
  async getPendingOrders (locationId) {
    const { rows } = await this.pgConnection.query(
      `
        with pending_orders as (
          select
            o.id,
            o.order_id,
            o.destination_id,
            o.supplier_id,
            o.created_at
          from avocado.data_location l
          left join avocado.data_order o on o.destination_id = l.fsid
          left join avocado.data_shipment s on o.order_id = s.order_id and o.destination_id = s.destination_id and o.supplier_id = s.origin_id
          where
            l.uuid = $1 and
            -- has no linked shipment which indicates no confirmation
            s.id is null and 
            -- order is listed as still open within a delivery window based off of the orders delivery date
            (
              coalesce(o.is_complete, false) is false AND
              o.delivery_date + '${ORDER_CONFIRMATION_WINDOW} week'::INTERVAL >= current_date
            )
        ),
        order_amount as (
          select
            po.order_id,
            po.destination_id,
            sum(coalesce(sp.sellprice, 0) * coalesce(ol.adjusted, 0)) as amount
          from pending_orders po
          left join avocado.data_orderline ol on po.id = ol.order_id 
          left join real.sellprice_daily sp on sp.sku = ol.sku and sp.date = po.created_at::date
          group by 1, 2
        ),
        invoice_amount as (
          select
            po.destination_id,
            po.order_id,
            sum(coalesce(sp.sellprice, 0) * coalesce(il.quantity, 0)) as amount
          from pending_orders po
          inner join avocado.data_invoice_line il on po.order_id = il.order_id
          left join avocado.data_invoice i on i.id = il.invoice_id
          left join avocado.data_invoice_quickbooks iq on iq.invoice_id = i.id
          left join real.sellprice_daily sp on sp.sku = il.sku and sp.date = po.created_at::date
          where il.quantity > 0
          group by 1, 2
        )
        select
          coalesce(o.destination_id, i.destination_id) as "locationId",
          coalesce(o.order_id, i.order_id) as "orderId",
          coalesce(o.amount, 0)::double precision as "deliveryAmount",
          coalesce(i.amount, 0)::double precision as "deliveryInvoiceAmount"
        from order_amount o
        left join invoice_amount i on o.destination_id = i.destination_id and o.order_id = i.order_id
      `,
      [locationId]
    )
    return rows
  }
}

module.exports = DataOrderAdapter
